import { getFullName } from "@/hooks/useProfile.ts"
import { branchDisplayName } from "@/lib/displayUtils.ts"
import { isUserPrincipal } from "@/lib/std.ts"
import { isPreviewPortBeingListened } from "@/routes/tasks/TaskViewScene/TaskPreviewModel.ts"

import type { AiAgentTask, AiAgentTaskContextItem, CPrincipal } from "@/lib/client/generated/TypeDefinitions.ts"
import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"

export function generateStructuredSummary(task: AiAgentTask, model: TaskViewModel): string {
  const title = task.title || "Untitled Task"

  const owner = getOwnerInfo(task.createdBy)
  const collaborators = getCollaboratorsInfo(task.collaborators || [])

  const artifacts = getArtifacts(task.relevantFiles || [], model)

  const functionalSummary = task.functionalSummary || ""
  const technicalSummary = task.technicalSummary || ""
  const definitionOfDone = task.definitionOfDone || ""

  if (functionalSummary.length === 0 && technicalSummary.length === 0 && definitionOfDone.length === 0) {
    return ""
  }

  const previewUrl = getPreviewUrl(model)

  const summaryParts: string[] = []

  summaryParts.push(`## 📝 ${title}\n`)
  summaryParts.push(`**Author**: ${owner}\n`)

  if (collaborators.length > 0) {
    summaryParts.push(`**Collaborators**: ${collaborators.join(", ")}\n`)
  }

  if (task.initialBranch) {
    summaryParts.push(`**Initial branch**: ${branchDisplayName(task.initialBranch)}\n`)
  }

  if (previewUrl) {
    summaryParts.push(`**Preview**: ${previewUrl}\n`)
  }

  summaryParts.push("\n")

  if (functionalSummary) {
    summaryParts.push("## 🎯 Recap\n")
    summaryParts.push(functionalSummary)
    summaryParts.push("\n\n")
  }

  if (definitionOfDone) {
    summaryParts.push("## ✅ Definition of Done\n")
    summaryParts.push(definitionOfDone)
    summaryParts.push("\n\n")
  }

  if (artifacts.length > 0) {
    summaryParts.push("## 📎 Artifacts\n")

    artifacts.forEach(artifact => {
      summaryParts.push(`- ${artifact.changeType}: \`${artifact.name}\`\n`)
    })

    summaryParts.push("\n")
  }

  if (technicalSummary) {
    summaryParts.push("## ⚙️ Technical Notes\n")
    summaryParts.push(technicalSummary)
    summaryParts.push("\n")
  }

  return summaryParts.join("")
}

function getOwnerInfo(createdBy: CPrincipal): string {
  if (!createdBy?.details) return "Unknown"

  if (isUserPrincipal(createdBy)) {
    return getFullName(createdBy.details.user)
  }

  return createdBy.name || "Unknown"
}

function getCollaboratorsInfo(collaborators: CPrincipal[]): string[] {
  return collaborators
    .filter(collaborator => isUserPrincipal(collaborator))
    .map(collab => {
      return getFullName(collab.details.user)
    })
}

interface Artifact {
  name: string
  changeType: string
}

function getArtifacts(relevantFiles: AiAgentTaskContextItem[], model: TaskViewModel): Artifact[] {
  const fileChangesMap = new Map<string, string>()

  const changesModel = model.changesModel
  if (changesModel) {
    const files = changesModel.files.get()
    files.forEach(diffFile => {
      if (diffFile.file) {
        const changeType = diffFile.file.changeType
        const filePath = diffFile.file.path || (diffFile.file.new?.path) || (diffFile.file.old?.path)
        if (filePath) {
          switch (changeType) {
            case "ADDED":
              fileChangesMap.set(filePath, "Added")
              break
            case "MODIFIED":
              fileChangesMap.set(filePath, "Modified")
              break
            case "DELETED":
              fileChangesMap.set(filePath, "Deleted")
              break
            default:
              fileChangesMap.set(filePath, "File")
          }
        }
      }
    })
  }

  const result = relevantFiles.map(file => {
    const fileName = file.name
    const changeType = fileChangesMap.get(fileName)

    if (file.type === "DIR") {
      return { name: fileName, changeType: "Related" }
    }

    return {
      name: fileName,
      changeType: changeType || "Related"
    }
  })

  return result.sort((a, b) => a.changeType.localeCompare(b.changeType))
}

function getPreviewUrl(model: TaskViewModel): string | null {
  const previewTabModel = model.previewTabModel.get()
  if (previewTabModel?.displayUrl.get()) {
    return previewTabModel.displayUrl.get()
  }

  const previewModel = model.currentPreview.get()
  if (previewModel && previewModel.previewProcessesState.get()) {
    const state = previewModel.previewProcessesState.get()
    if (state && state.className === "Running" && isPreviewPortBeingListened(state)) {
      return state.url
    }
  }

  return null
}
